
<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { useGlobalData } from '@/utils/useGlobal';

const { isShowVideo, isPlayBgm, videoUrl } = useGlobalData()

const props = defineProps({
  src: {
    type: String,
    default: "",
  },
  percent: {
    type: Number,
    default: 80,
  },
  maxWidth: {
    type: Number,
    default: 0,
  },
  maxHeight: {
    type: Number,
    default: 0,
  },
  closeOnMaskClick: {
    type: Boolean,
    default: false,
  },
  controls: {
    type: Boolean,
    default: true,
  },
  styleCus: {
    type: String,
    default: "",
  }
})

const video = ref<HTMLVideoElement | null>(null);
const isPlaying = ref(false); // 新增标志变量，防止多次调用 play()

const playerStyle = computed(() => [
  props.maxWidth ? `max-width:${props.maxWidth}px` : '',
  props.maxHeight ? `max-height:${props.maxHeight}px` : '',
  props.percent ? `width:${props.percent}%;height:${props.percent}%` : ''
].filter(css => !!css).join(';'));

function getBrowserType() {
  const isBrowser = {
    qqBrowser: false,
    ios: false
  }
  try { 
    const n = navigator.userAgent;
    if (n) {
      isBrowser.qqBrowser = n.toLowerCase().indexOf("mqqbrowser") > -1 || n.toLowerCase().indexOf("qqtheme") > -1;
      isBrowser.ios = !!n.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/);
    }
  } catch(error){console.log(error)}
  return isBrowser
}
let playVideoPromise: Promise<unknown> | null = null;

function isPromiseLike(data: unknown): data is Promise<unknown> {
  return typeof data === "object" && !!data && "then" in data && typeof data.then === "function"
}
function play() {
  if (isPlaying.value) return; // 防止多次调用 play()
  isPlaying.value = true;
  isShowVideo.value = true;
  nextTick(playVideo);
}

function playVideo() {
  
  if (video.value) {
    video.value.disablePictureInPicture = true;
    if (video.value.readyState !== 4) {
      video.value.load();
    }
    if (video.value.readyState < 4) {
      video.value.addEventListener('canplay', onCanPlay);
    } else {
      onCanPlay();
    }
  }
}

function onCanPlay() {
  if (video.value) {
    const result = video.value.play();
    if (isPromiseLike(result)) {
      playVideoPromise = result;
    }
    playVideoPromise && playVideoPromise.catch((e: any) => {
      console.error("播放失败:", e);
      isPlaying.value = false; // 播放失败后重置标志变量
    });
    isPlayBgm.value = false;
  }
}

function close(fromCloseBtn: boolean) {
  if (fromCloseBtn === true || props.closeOnMaskClick) {
    videoUrl.value = '';
    stopVideo();
    updateUrlIfNecessary();
  }
}

function stopVideo() {
  if (playVideoPromise) {
    playVideoPromise
      .then((_: any) => {
        video.value && video.value.pause();
      })
      .catch((error: any) => {
        console.log(error);
      });
  }
  isShowVideo.value = false;
  isPlaying.value = false; // 停止播放后重置标志变量
}

function updateUrlIfNecessary() {
  try {
    getBrowserType().qqBrowser &&
      getBrowserType().ios &&
      window.history.replaceState(
        null,
        "",
        window.location.href.split("?")[0] +
        "?_t=" +
        (function (n) {
          return Math.floor(Math.random() * n) + 1;
        })(9999999)
      );
  } catch(error){console.log(error)}
}

watch(
  () => isShowVideo.value,
  (value) => {
    if (value) {
      play();
    }
  }
);

</script>
<template>
  <div
    v-show="isShowVideo"
    class="video-player-box"
    @click="close(false)"
  >
    <div class="video-player" :style="playerStyle">
      <span @click.stop.prevent="close(true)"><i>close</i></span>
      <video
        v-if="src"
        ref="video"
        :src="src"
        preload="preload"
        playsinline
        x5-video-player-type="h5"
        x5-video-player-fullscreen="true"
        x5-video-orientation="portraint"
        webkit-playsinline="true"
        x5-playsinline
        :controls="controls"
        controlslist="nodownload noremoteplayback"
        disablepictureinpicture=""
      />
      <div v-else class="no-video-holder">
        <p>缺少视频链接配置</p>
      </div>
    </div>
  </div>
</template>

<style lang="less" scoped>
.video-player-box {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 100;
  display: flex;
  align-content: center;
  align-items: center;
  justify-content: center;
  background: rgba(8, 8, 11, 0.8);
  .video-player {
    position: relative;
    display: inline-block !important;
    width: 80%;
    height: 80%;
    background-color: #000;
    span {
      position: absolute;
      top: 0;
      right: -0.45rem; /* 原始的 -0.45rem * 100 */
      z-index: 1;
      display: block;
      width: 0.36rem; /* 原始的 0.36rem * 100 */
      height: 0.36rem; /* 原始的 0.36rem * 100 */
      cursor: pointer;
      background: url("../../../assets/images/close.png") no-repeat center;
      background-size: contain;
      transition: transform 0.5s;
      transform: rotate(0deg);
  
      i {
        display: none;
      }
  
      &:hover {
        transform: rotate(180deg);
      }
    }

    video {
      max-width: 100%;
      height: 100%;
      margin: auto;
      object-fit: contain;
    }
  }
}
.pc {
  .video-player {
    width: 12.8rem !important;
    height: 7.2rem !important;
  }
}
.wap {
  .video-player {
    width: 7.5rem !important;
    height: 5rem !important;
    span {
      top: -0.4rem;
      right: 0.2rem;
    }
  }
}
</style>
